import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropdown", "badge", "list", "loading"]
  static values = {
    open: { type: Boolean, default: false },
    loaded: { type: Boolean, default: false }
  }

  connect() {
    this.closeOnClickOutside = this.closeOnClickOutside.bind(this)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnClickOutside)
  }

  toggle(event) {
    event.stopPropagation()
    this.openValue = !this.openValue
  }

  openValueChanged() {
    if (this.openValue) {
      this.show()
    } else {
      this.hide()
    }
  }

  show() {
    this.dropdownTarget.classList.add("is-open")
    document.addEventListener("click", this.closeOnClickOutside)

    if (!this.loadedValue) {
      this.loadNotifications()
    }
  }

  hide() {
    this.dropdownTarget.classList.remove("is-open")
    document.removeEventListener("click", this.closeOnClickOutside)
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.openValue = false
    }
  }

  async loadNotifications() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }

    try {
      const response = await fetch("/notifications", {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const html = await response.text()
        this.listTarget.innerHTML = html
        this.loadedValue = true
      }
    } catch (error) {
      console.error("Failed to load notifications:", error)
      this.listTarget.innerHTML = '<div class="notification-empty">Failed to load</div>'
    } finally {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add("hidden")
      }
    }
  }

  async markAsRead(event) {
    const notificationId = event.currentTarget.dataset.notificationId
    const item = event.currentTarget.closest(".notification-item")

    try {
      const response = await fetch(`/notifications/${notificationId}/read`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "text/html"
        }
      })

      if (response.ok) {
        item.classList.remove("is-unread")
        item.classList.add("is-read")
        this.updateBadge()
      }
    } catch (error) {
      console.error("Failed to mark as read:", error)
    }
  }

  async markAllAsRead(event) {
    event.preventDefault()

    try {
      const response = await fetch("/notifications/read_all", {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "text/html"
        }
      })

      if (response.ok) {
        // Mark all items as read
        this.listTarget.querySelectorAll(".notification-item.is-unread").forEach(item => {
          item.classList.remove("is-unread")
          item.classList.add("is-read")
        })
        this.hideBadge()
      }
    } catch (error) {
      console.error("Failed to mark all as read:", error)
    }
  }

  async updateBadge() {
    try {
      const response = await fetch("/notifications/unread_count", {
        headers: { "Accept": "text/html" }
      })

      if (response.ok) {
        const html = await response.text()
        if (this.hasBadgeTarget) {
          this.badgeTarget.outerHTML = html
        }
      }
    } catch (error) {
      console.error("Failed to update badge:", error)
    }
  }

  hideBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.add("hidden")
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
